// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useContractStore from '@/app/store/modules/contract';
import actionsContracts from '@/app/modules/contracts/api/actionsContracts';
// Libs
import { cn } from '@/common-utils/libraries/tailwind/utils';
// Components
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import Entity from '@/common-utils/components/renders/entity/Entity';
import ContractsFromProviderTable from '@/app/modules/contracts/components/tables/ContractsFromProviderTable';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
import RanksUpdate from '@/app/modules/contracts/components/ranksUpdate/RanksUpdate';
// Common
import { excludeRepeatedValues } from '@/common-utils/functions/repeatedValues';
import getInitialValueForRowSelected from '@/common-utils/components/renders/dataTable/utils/GetInitialValueForRowSelected';
import { useToast } from '@/common-utils/hooks/use-toast';
import useStepConfig from '@/common-utils/hooks/useStepConfig';
// Lang
import { nsAccount } from '@/app/i18n/namespaces';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.merchantAccountId
 * @param root0.merchantAccountDetails
 * @returns {JSX} ModalAddContracts
 */
function ModalAddContracts({ isOpen, setIsOpen, merchantAccountId, merchantAccountDetails }) {

    // Constants
    const STEPS_NUMBER = 3;
    const STEP_TITLE_PREFIX = 'modal.add_contracts.steps';

    // Toast
    const { toast } = useToast();

    // Store
    const storeRefetchContracts = useContractStore(s => s.storeRefetchContracts);

    // State
    const [step, setStep] = useState(0);
    const [contractsOnError, setContractsOnError] = useState([]);

    // Table selection
    const [contractsSelected, setContractsSelected] = useState(getInitialValueForRowSelected());

    // API Calls
    const onSuccess = () => {
        storeRefetchContracts(true);
        setIsOpen(false);
        toast({
            description: <Entity entity='modal.add_contracts.success' namespace={nsAccount} />,
            variant: 'success'
        });
    };
    const onError = () => {
        setIsOpen(false);
        toast({
            description: <Entity entity='modal.add_contracts.error' namespace={nsAccount} />,
            variant: 'danger'
        });
    };
    const submitAddContracts = actionsContracts.addContracts(onSuccess, onError);

    // Hooks
    const { stepTitle, disabledNext } = useStepConfig(
        step,
        () => ({
            0: { stepTitle: `${STEP_TITLE_PREFIX}.contract_selection`, disabledNext: contractsSelected.rowsData.length === 0 },
            1: { stepTitle: `${STEP_TITLE_PREFIX}.contract_edition`, disabledNext: false },
            2: { stepTitle: `${STEP_TITLE_PREFIX}.confirmation`, disabledNext: false }
        }),
        [contractsSelected.rowsData]
    );

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className={cn(step !== 0 ? 'max-w-3xl' : 'max-w-6xl')}>
                <HeaderTitle title='modal.add_contracts.title' namespace={nsAccount} />
                <ModalBody className='modal-max-height overflow-auto'>
                    <Entity
                        entity={stepTitle}
                        data={{ step: step + 1, maxStep: STEPS_NUMBER }}
                        namespace={nsAccount}
                        className={'mb-2 flex'}
                    />
                    {
                        step === 0 &&
                        <ContractsFromProviderTable
                            filtersEventName={setFiltersEventName('contracts_from_provider', true)}
                            rowsSelected={contractsSelected}
                            setRowsSelected={setContractsSelected}
                        />

                    }
                    {
                        step === 1 &&
                        contractsSelected.rowsData && excludeRepeatedValues(contractsSelected.rowsData,
                            contract => [contract.contract, contract.application, contract.number, contract.bankCode]).map((contract) => (
                                <RanksUpdate contract={contract} key={contract.uuid} contractsOnError={contractsOnError} setContractsOnError={setContractsOnError} />
                            ))
                    }
                    {
                        step === 2 &&
                        <>
                            <Entity
                                entity={'modal.add_contracts.texts.confirmation'}
                                namespace={nsAccount}
                                data={{ label: merchantAccountDetails.label, contracts: contractsSelected.rowsData.length }}
                            />
                            <div className='mt-2'>
                                {
                                    contractsSelected.rowsData && excludeRepeatedValues(contractsSelected.rowsData,
                                        contract => [contract.contract, contract.application, contract.number, contract.bankCode]).map((contract) => (
                                            <div className='ml-8' key={contract.uuid}>
                                                <Entity
                                                    entity={'modal.add_contracts.texts.contract'}
                                                    namespace={nsAccount}
                                                    data={{ contractNumber: contract.number }}
                                                />
                                            </div>
                                        ))
                                }
                            </div>
                        </>
                    }

                </ModalBody>
                <FooterStep
                    step={step}
                    setStep={setStep}
                    stepsNumber={STEPS_NUMBER}
                    onValidate={() => {
                        submitAddContracts.mutate({
                            merchantAccountUuid: merchantAccountId,
                            contracts: contractsSelected.rowsData.map((contract => ({
                                contract: contract.uuid,
                                merchantLabel: contract.merchantLabel ?? null,
                                rank: contract.newRank?.includes(',') ? contract.newRank.replaceAll(' ', '').split(',') : contract.newRank
                            })))
                        });
                    }
                    }
                    isLoading={submitAddContracts.isLoading}
                    disableValidation={disabledNext || (step === 1 && contractsOnError.length > 0)}
                />
            </ModalContent>
        </Modal>
    );
}

ModalAddContracts.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    merchantAccountId: PropTypes.string.isRequired,
    merchantAccountDetails: PropTypes.object.isRequired
};

export default ModalAddContracts;